import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchChallenge, isAntifraudEnabled } from "./api";
import { PowProgress } from "./PowProgress";
import { solvePow } from "./powSolve";
import { createSignalCollector } from "./signals";
import { SliderPuzzle } from "./SliderPuzzle";

export function useAntifraudTicket({ challengeUrl, active }) {
  const [ticket, setTicket] = useState(null);
  const [honeypot, setHoneypot] = useState("");
  const [powWaiting, setPowWaiting] = useState(false);
  const [powPercent, setPowPercent] = useState(0);
  const [puzzle, setPuzzle] = useState(null);
  const ticketRef = useRef(null);
  const abortRef = useRef(null);
  const powPromiseRef = useRef(Promise.resolve(null));
  const powPendingRef = useRef(false);
  const powNonceRef = useRef(null);
  const powMsRef = useRef(null);
  const signalsRef = useRef(null);
  const puzzleRef = useRef(null);
  const interactiveRef = useRef(null);
  const puzzleWaitersRef = useRef([]);
  ticketRef.current = ticket;

  const startPow = useCallback((data) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    powNonceRef.current = null;
    powMsRef.current = null;
    setPowPercent(0);
    if (!data || data.kind !== "pow" || !data.pow) {
      powPendingRef.current = false;
      powPromiseRef.current = Promise.resolve(null);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    powPendingRef.current = true;
    const started = Date.now();
    powPromiseRef.current = solvePow(data.pow, {
      signal: controller.signal,
      onProgress: setPowPercent,
    }).then((nonce) => {
      powNonceRef.current = nonce;
      powMsRef.current = Date.now() - started;
      powPendingRef.current = false;
      return nonce;
    });
  }, []);

  const load = useCallback(
    async (renew, escalate) => {
      if (!isAntifraudEnabled() || !active || !challengeUrl) {
        return;
      }
      try {
        const data = await fetchChallenge(challengeUrl, { renew, escalate });
        const next = data?.ticket || null;
        ticketRef.current = next;
        setTicket(next);
        if (data?.collect_signals) {
          if (!signalsRef.current) {
            signalsRef.current = createSignalCollector();
          }
          signalsRef.current.start();
        } else if (signalsRef.current) {
          signalsRef.current.stop();
        }
        if (data && data.kind === "interactive" && data.interactive) {
          puzzleRef.current = data.interactive;
          interactiveRef.current = null;
          setPuzzle(data.interactive);
        } else {
          puzzleRef.current = null;
          interactiveRef.current = null;
          setPuzzle(null);
        }
        startPow(data);
      } catch (_error) {
        ticketRef.current = null;
        setTicket(null);
        puzzleRef.current = null;
        interactiveRef.current = null;
        setPuzzle(null);
        startPow(null);
      }
    },
    [challengeUrl, active, startPow]
  );

  useEffect(() => {
    if (!active || !isAntifraudEnabled()) {
      return undefined;
    }
    load();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (signalsRef.current) {
        signalsRef.current.stop();
      }
    };
  }, [active, load]);

  const getEnvelope = useCallback(async () => {
    if (powPendingRef.current) {
      setPowWaiting(true);
    }
    try {
      const nonce = await powPromiseRef.current;
      const signals =
        signalsRef.current && signalsRef.current.listening
          ? await signalsRef.current.snapshot()
          : undefined;
      return {
        ticket: ticketRef.current,
        honeypot,
        pow_nonce: nonce || undefined,
        pow_ms: powMsRef.current == null ? undefined : powMsRef.current,
        interactive: interactiveRef.current || undefined,
        signals,
      };
    } finally {
      setPowWaiting(false);
    }
  }, [honeypot]);

  const waitPuzzleIfNeeded = useCallback(() => {
    if (!puzzleRef.current) {
      return Promise.resolve();
    }
    if (interactiveRef.current) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      puzzleWaitersRef.current.push(resolve);
    });
  }, []);

  const clearPuzzleAnswer = useCallback(() => {
    interactiveRef.current = null;
  }, []);

  const onPuzzleCommit = useCallback((answer) => {
    interactiveRef.current = answer;
    const waiters = puzzleWaitersRef.current;
    puzzleWaitersRef.current = [];
    waiters.forEach((resolve) => resolve());
  }, []);

  return {
    getEnvelope,
    renew: (opts) => load(ticketRef.current, opts && opts.escalate),
    waitPuzzleIfNeeded,
    clearPuzzleAnswer,
    markUsed: () => {
      ticketRef.current = null;
      setTicket(null);
      return load();
    },
    powProgressProps: { visible: powWaiting, value: powPercent },
    powSlot: (
      <>
        <PowProgress visible={powWaiting} value={powPercent} />
        {puzzle ? (
          <SliderPuzzle
            image={puzzle.image}
            piece={puzzle.piece}
            pieceY={puzzle.piece_y}
            width={puzzle.width}
            height={puzzle.height}
            pieceSize={puzzle.piece_size}
            onCommit={onPuzzleCommit}
            onRefresh={() => load(ticketRef.current, true)}
          />
        ) : null}
      </>
    ),
    honeypotProps: {
      name: "af_hp",
      value: honeypot,
      onChange: (event) => setHoneypot(event.target.value),
      tabIndex: -1,
      "aria-hidden": true,
      autoComplete: "off",
      className: "af-hp",
    },
  };
}
